/**
 * preferencesSync.js — automatic sync of user preferences between
 * localStorage (fast, per-browser) and the backend (persistent,
 * per-user, follows across devices).
 *
 * HOW IT WORKS:
 *   1. On init: fetch GET /api/me/preferences → merge into localStorage
 *      (server values win for keys that exist on both sides).
 *   2. On any localStorage write to a synced key: collect dirty keys
 *      and debounce-save to PUT /api/me/preferences.
 *
 * USAGE:
 *   import { initPreferencesSync } from './preferencesSync';
 *   // Call once after login / app mount:
 *   await initPreferencesSync();   // resolves once the account is hydrated
 *
 * Components keep using localStorage.getItem / setItem as-is.
 * No component changes needed.
 *
 * WHICH KEYS SYNC — everything, unless it is listed as local-only below.
 *
 *   This used to be the other way round: a hand-typed allowlist of keys
 *   that follow the account. That list was how settings kept drifting
 *   between laptops. Every new preference anyone added — the bold-borders
 *   theme switch, the per-project link palette, the stamp toolbox
 *   options, the object reveal-on-hover toggles, panel sides, the
 *   Classes page layout, the OCR run options, and dozens more — was
 *   born browser-local, because nobody remembered to come back here and
 *   list it. Each one looked fine on the laptop it was set on and
 *   "wrong" on every other.
 *
 *   Syncing by default inverts the failure: a new key follows the
 *   account unless someone deliberately says it must not. The things
 *   that genuinely must stay local are few and knowable — auth, caches,
 *   multi-megabyte blobs that have their own endpoint, and screen
 *   positions that would land off-screen on a different display — and
 *   those are the LOCAL_ONLY lists below. A size cap catches anything
 *   that would bloat the preferences payload.
 */

import apiFetch from './apiFetch';
import { initTheme } from './theme';
import { rawSetItem, rawRemoveItem } from './rawStorage';
import { syncSymbolsFromServer } from '../components/shared/useMarkupSymbols';
import { getStoredUser } from './authStorage';

// Which account this browser last hydrated in full. A browser that already
// holds that account's settings need not wait for them again before the
// first paint (a phone on mobile data waited up to six seconds, every visit,
// for a copy it already had); the hydrate still runs and lands behind it.
const SEEN_KEY = 'pidly.prefsSeenFor';
export function hydratedBeforeHere() {
  try {
    const id = getStoredUser()?.id;
    return !!id && localStorage.getItem(SEEN_KEY) === String(id);
  } catch { return false; }
}

// ── Keys that must NOT sync ───────────────────────────────────────────
// Exact keys. Auth/session state and one-shot flags for THIS browser.
const LOCAL_ONLY_KEYS = new Set([
  'authToken',
  'authenticated',
  'pidly.prefsSeenFor',
  'user',
  'msalAccount',
  'pidly.postLoginRedirect',
  'pidly.pendingNewFromLink',
  // Debug switch — you turn it on for one machine, not your account.
  'pidly.perfLog',
  // The stamp library lives in IndexedDB + its own /api/me/symbols
  // endpoint (megabytes of PNGs); the legacy localStorage copy is a
  // migration leftover and must never ride the preferences payload.
  'markup_symbols',
  // Smart parts: server-backed per project; localStorage is only the
  // cold-start cache (and can carry multi-MB stamp data URLs).
  'pidly_smart_parts',
  'pidly_sp_previews',
  // Screen-space positions/sizes of floating things. Synced, they land
  // off-screen on a laptop after being dragged on a monitor.
  'pidly.chatFloatPos',
  'pidly.chatFloatSize',
  'pidly.floatingControlPanel.pos',
  // The admin's avatar palette is SITE-wide (GET /api/avatar-palette); this
  // key is only this browser's cold-start copy of it. Synced, each account
  // carried its own stale copy and the hydrate wrote it back over the fresh
  // one, so other people kept the old colours.
  'pidly.avatarPalette',
]);

// Key families (prefix match). Caches, big derived blobs, per-browser
// one-shot markers, and third-party libraries' own storage.
const LOCAL_ONLY_PREFIXES = [
  // userScoped variants of the screen-position keys above
  // (`pidly.chatFloatPos.<userId>` etc.).
  'pidly.chatFloatPos',
  'pidly.chatFloatSize',
  'pidly.floatingControlPanel.pos',
  // Caches / derived data — regenerated, never a preference.
  'pidly.wsThumb.',          // workspace thumbnails (images)
  'pidly.pname.',            // project-name cache for titles
  'pidly.jobReported.',      // "this job's toast was shown" markers
  // NOTE deliberately NOT local-only: `fileDisplayConfigPushApplied_<id>`
  // ("this user has adopted admin push X"). It was, and that is exactly
  // how a fresh browser lost your settings: the user's own
  // `fileDisplayConfig_<id>` syncs with the account, but with the marker
  // left behind the new browser believed the admin push had never been
  // applied and wrote it straight over the synced personal copy — turning
  // warnings and card choices back on in every incognito window. The
  // marker is per USER, not per browser, so it travels with the config it
  // protects.
  'markup_backup_',          // markup auto-backup (whole markup sets)
  'ocr_results_',            // OCR output cache
  'pidly_auditMissing_',     // audit scratch
  'pidly_smart_parts_',      // per-project smart-parts cache (server-backed)
  'pidly.adminEditMode.',    // admin edit toggle for this browser
  'pidly.recentDocs',        // this browser's recents seed; the server list is the truth
                             // (another device's copy overwrote it and Jump Back In flipped)
  // Third-party: MSAL keeps tokens/accounts in localStorage.
  'msal',
  // Tooling
  'debug',
  'loglevel',
  '__',
];

// Patterns for third-party keys a prefix can't express (MSAL credential
// entries are `<homeAccountId>-login.windows.net-accesstoken-...`).
const LOCAL_ONLY_REGEXES = [
  /msal/i,
  /login\.(windows|microsoftonline)\./i,
  /-(accesstoken|idtoken|refreshtoken|account|appmetadata)-/i,
  // Anything about a Quick View (`project_quick_<id>`): it is a throwaway
  // viewer that must leave no trace, so none of its per-view keys (display
  // config, sidebar width, last file) may ever reach the account.
  /project_quick_/,
];

// Anything bigger than this is not a preference, it is data — and it
// would make every hydrate pay for it. 64 KB is roughly 30× the largest
// legitimate preference (a per-project region view preset list).
const MAX_VALUE_BYTES = 64 * 1024;

export function isSyncedKey(key) {
  if (typeof key !== 'string' || !key) return false;
  if (LOCAL_ONLY_KEYS.has(key)) return false;
  for (const prefix of LOCAL_ONLY_PREFIXES) {
    if (key.startsWith(prefix)) return false;
  }
  for (const rx of LOCAL_ONLY_REGEXES) {
    if (rx.test(key)) return false;
  }
  return true;
}

function isTooBig(raw) {
  return typeof raw === 'string' && raw.length > MAX_VALUE_BYTES;
}

// ── State ─────────────────────────────────────────────────────────────
let initialized = false;
let initPromise = null;
// True once the account's preferences have landed in localStorage (or the
// fetch failed and we're carrying on local-only). Until then, nothing is
// pushed to the server: a flush of pre-hydrate values would overwrite the
// account with whatever this browser last had, which is exactly the
// stale-laptop-wins clobber this module exists to prevent.
let hydrated = false;
// True once a hydrate actually READ the account (the server answered with
// preferences). A hydrate refused as signed out (401 — ProtectedRoute's
// effect also runs on the render that redirects a signed-out visitor to
// /login) must not count, or the account's theme and settings never land
// after sign-in until a reload.
let serverHydrated = false;
let authRefused = false;
let saveTimer = null;
let dirtyKeys = new Set();
const SAVE_DEBOUNCE_MS = 2000; // 2 seconds after last change

export function isPreferencesHydrated() { return hydrated && !authRefused; }

/** Resolves once hydration is done (or has given up). Resolves at once if
 *  sync was never started, so callers can't hang on it. */
export function whenPreferencesHydrated() {
  return initPromise || Promise.resolve();
}

// Fired on the window once the account's preferences have landed. A
// setting read at mount from this browser's own copy re-reads on it: the
// first paint does not always wait for the account (hydratedBeforeHere).
export const PREFS_HYDRATED_EVENT = 'pidly:preferences-hydrated';

// ── Hydrate: server → localStorage ────────────────────────────────────
async function hydrate() {
  let serverKeys = [];
  try {
    const r = await apiFetch('/api/me/preferences');
    authRefused = r.status === 401;
    const d = await r.json();
    if (d.success && d.preferences) {
      serverHydrated = true;
      try { const id = getStoredUser()?.id; if (id) rawSetItem(SEEN_KEY, String(id)); } catch { /* storage full */ }
      const prefs = d.preferences;
      for (const key of Object.keys(prefs)) {
        if (!isSyncedKey(key)) continue;
        const serverVal = prefs[key];
        if (serverVal === undefined || serverVal === null) continue;
        const strVal = typeof serverVal === 'string' ? serverVal : JSON.stringify(serverVal);
        // rawSetItem, not the patched setItem: landing the server's own
        // values must not mark them dirty and PUT the whole lot straight
        // back.
        rawSetItem(key, strVal);
        serverKeys.push(key);
      }
      console.log(`[Prefs] Hydrated ${serverKeys.length} preferences from server`);
    }
  } catch (err) {
    console.warn('[Prefs] Could not hydrate from server:', err.message);
  }

  // Anything written while we were waiting is a pre-hydrate value — this
  // browser's old copy, or a mount effect echoing default state. Where
  // the server had a value it has just overwritten local and must not be
  // pushed back; where it didn't, the local write is the only copy and
  // can go up.
  for (const k of serverKeys) dirtyKeys.delete(k);
  hydrated = true;
  if (dirtyKeys.size) scheduleSave();

  // initTheme() already ran at boot from local storage; now that the
  // account's theme has been hydrated, re-apply it so a fresh device /
  // browser picks up the user's chosen theme without a reload.
  try { initTheme(); } catch (e) { console.warn('[Prefs] theme re-apply failed:', e?.message || e); }
  if (serverKeys.length) {
    try { window.dispatchEvent(new CustomEvent(PREFS_HYDRATED_EVENT, { detail: { keys: serverKeys } })); } catch { /* no window */ }
  }
  // The stamp library has its own endpoint; pull it now that we know we
  // are signed in (its module-load pull ran before login and got a 401).
  try { syncSymbolsFromServer(); } catch { /* best effort */ }
}

// ── Persist: localStorage → server ────────────────────────────────────
// Keys worth saving the instant they change rather than after the
// debounce: deliberate, infrequent choices where losing the write to a
// quick reload is very visible. The theme is the case that prompted it.
const IMMEDIATE_PREFIXES = ['pidly.theme', 'pidly.boldBorders'];
function isImmediateKey(key) {
  return IMMEDIATE_PREFIXES.some(p => key.startsWith(p));
}

function scheduleSave(immediate = false) {
  if (!hydrated || authRefused) return; // hydrate() reschedules once it lands
  if (saveTimer) clearTimeout(saveTimer);
  if (immediate) {
    saveTimer = null;
    flushToServer();
    return;
  }
  saveTimer = setTimeout(flushToServer, SAVE_DEBOUNCE_MS);
}

function buildPayload() {
  const payload = {};
  for (const key of dirtyKeys) {
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) {
        payload[key] = null; // deleted
      } else if (isTooBig(raw)) {
        console.warn(`[Prefs] Not syncing "${key}": ${raw.length} bytes is data, not a preference`);
      } else {
        // Try to parse JSON values so they're stored as objects, not strings
        try { payload[key] = JSON.parse(raw); } catch { payload[key] = raw; }
      }
    } catch { /* unreadable */ }
  }
  dirtyKeys.clear();
  return payload;
}

async function flushToServer() {
  if (!hydrated || authRefused || dirtyKeys.size === 0) return;
  const payload = buildPayload();
  if (Object.keys(payload).length === 0) return;

  try {
    await apiFetch('/api/me/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.warn('[Prefs] Could not save to server:', err.message);
    // Re-add keys as dirty so next save retries
    for (const key of Object.keys(payload)) dirtyKeys.add(key);
  }
}

// ── Intercept localStorage.setItem ────────────────────────────────────
// Monkey-patch localStorage.setItem to detect writes to synced keys.
// This is the zero-change approach: existing components keep calling
// localStorage.setItem and the sync happens automatically.
const _originalSetItem = rawSetItem;
const _originalRemoveItem = rawRemoveItem;

function patchedSetItem(key, value) {
  _originalSetItem(key, value);
  if (initialized && isSyncedKey(key)) {
    dirtyKeys.add(key);
    scheduleSave(isImmediateKey(key));
  }
}

function patchedRemoveItem(key) {
  _originalRemoveItem(key);
  if (initialized && isSyncedKey(key)) {
    dirtyKeys.add(key);
    scheduleSave(isImmediateKey(key));
  }
}

// ── Init ──────────────────────────────────────────────────────────────
/** Starts sync (idempotent) and resolves once the account's preferences
 *  have been hydrated into localStorage. The app waits on this before
 *  rendering anything that reads a preference, so first paint on a new
 *  laptop shows the ACCOUNT's settings rather than the browser's empty
 *  defaults — and, worse, then saving those defaults over the account. */
export function initPreferencesSync() {
  if (initialized) {
    // The first hydrate was refused as signed out: read the account now
    // that we are signed in, instead of handing back that empty result.
    if (authRefused && !serverHydrated) {
      authRefused = false;
      hydrated = false; // nothing goes up until the account's copy is in
      initPromise = hydrate();
    }
    return initPromise;
  }
  initialized = true;

  // Patch localStorage
  localStorage.setItem = patchedSetItem;
  localStorage.removeItem = patchedRemoveItem;

  // Flush when the page goes away, so a change made in the last couple of
  // seconds isn't lost.
  //
  // This used to use navigator.sendBeacon and never worked: it posted to a
  // RELATIVE '/api/me/preferences' (the frontend origin, not BACKEND_URL),
  // and it built an Authorization header that sendBeacon cannot send — the
  // API takes headers for the Blob's content type, not for the request. So
  // every unload save was either a 404 on the wrong host or an
  // unauthenticated 401. fetch with keepalive does the same job and can
  // carry the bearer token, which is what apiFetch attaches.
  //
  // pagehide rather than beforeunload: beforeunload is unreliable on
  // mobile and is skipped entirely by the bfcache. visibilitychange
  // catches tab-switch-then-kill.
  const flushOnLeave = () => {
    if (!hydrated || authRefused || dirtyKeys.size === 0) return;
    const payload = buildPayload();
    if (Object.keys(payload).length === 0) return;
    try {
      apiFetch('/api/me/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true,
      }).catch(() => { /* leaving anyway */ });
    } catch { /* leaving anyway */ }
  };
  window.addEventListener('pagehide', flushOnLeave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushOnLeave();
  });

  console.log('[Prefs] Sync initialized');
  initPromise = hydrate();
  return initPromise;
}

// ── Manual flush (call before logout) ─────────────────────────────────
export async function flushPreferences() {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
  await flushToServer();
}
